import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { AuthModalService } from '../../../../shared/services/auth-modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { retryAfterSeconds } from '../../../../shared/services/newsletter.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { InputComponent } from '../../../../shared/ui/input/input.component';
import { AUTH_COPY } from '../../auth.copy';

/** Espera mínima tras un 429 (la misma que el botón de ajustes del perfil): `max(60, Retry-After)`. */
const WAIT_FLOOR_S = 60;

/** Mensaje del servidor (`{"error": "<texto>"}`) si es un string no vacío; si no, `null`. */
function serverMessage(err: HttpErrorResponse): string | null {
  const message = (err.error as { error?: unknown } | null)?.error;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

@Component({
  selector: 'app-forgot-password-form',
  imports: [ReactiveFormsModule, InputComponent, ButtonComponent],
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = AUTH_COPY.FORGOT;

  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly showNoAccountHint = signal(false);
  protected readonly sent = signal(false);
  protected readonly sentToEmail = signal('');

  /** Fin absoluto de la espera (ms epoch), como el store de ajustes: nunca un contador decrementado. */
  private readonly until = signal(0);
  private readonly now = signal(Date.now());
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Segundos de espera que quedan (0 = libre), redondeando hacia arriba. */
  protected readonly remaining = computed(() =>
    Math.max(0, Math.ceil((this.until() - this.now()) / 1000)),
  );
  protected readonly waitLabel = computed(() =>
    this.copy.LINK_WAIT.replace('{s}', String(this.remaining())),
  );

  protected readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTicker());
  }

  protected onSubmit(): void {
    // Guarda propia: no depende solo del `disabled` del botón (Enter en el campo también envía).
    if (this.remaining() > 0) return;

    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    const { email } = this.form.getRawValue();
    this.loading.set(true);
    this.errorMessage.set(null);
    this.showNoAccountHint.set(false);

    this.authService
      .forgotPassword(email)
      // Último operador: una respuesta tardía (modal cerrado) no toca un formulario destruido ni le arma un reloj.
      .pipe(finalize(() => this.loading.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.sentToEmail.set(email);
          this.sent.set(true);
        },
        error: (err: unknown) => this.onError(err),
      });
  }

  protected switchToLogin(): void {
    this.authModalService.switchMode('login');
  }

  protected switchToRegister(): void {
    this.authModalService.switchMode('register');
  }

  private onError(err: unknown): void {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 429) {
        this.errorMessage.set(serverMessage(err) ?? this.copy.GENERIC_ERROR);
        this.startWait(Math.max(WAIT_FLOOR_S, retryAfterSeconds(err.headers.get('Retry-After')) ?? 0));
        return;
      }

      if (err.status === 422) {
        // «Sin cuenta» (texto del servidor): se ofrece crearla. Un 422 de validación trae `error` como objeto
        // → genérico y sin ayuda. No salió correo: sin espera.
        const message = serverMessage(err);
        this.errorMessage.set(message ?? this.copy.GENERIC_ERROR);
        this.showNoAccountHint.set(message !== null);
        return;
      }
    }

    this.errorMessage.set(this.copy.GENERIC_ERROR);
  }

  private startWait(seconds: number): void {
    const now = Date.now();
    this.now.set(now);
    this.until.set(now + seconds * 1000);
    this.ensureTicker();
  }

  /** Reloj de 1 s solo en navegador; se para solo al llegar a 0. */
  private ensureTicker(): void {
    if (!this.isBrowser || this.timer !== null) return;
    this.timer = setInterval(() => {
      const now = Date.now();
      this.now.set(now);
      if (this.until() <= now) this.stopTicker();
    }, 1000);
  }

  private stopTicker(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
